import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Moment from "react-moment";
import ContentContainer from "../../templates/content-container/contentContainer";
import Loading from "../../ui-components/loading/loading";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import LabeledValue, {
  LabeledValueDirection,
  LabeledValueValueSize,
} from "../../ui-components/molecules/labeled-value/labeledValue";
import PdfActionSplitButton from "../../ui-components/pdf-action-split-button/pdfActionSplitButton";
import {
  deliverPdf,
  getPdfDownloadErrorMessage,
} from "../../app/data/shipping-docs/downloadPdf";
import { PdfDocumentAction } from "../../app/data/shipping-docs/pdfDocumentActionStorage";
import PaymentService from "../../app/data/payment/paymentService";
import { PaymentReceipt } from "../../app/data/payment/PaymentSession";
import { InvoicesPaid, PaymentSummary } from "./paymentAmountBreakdown";
import "../invoices/invoice-payment/paymentReturn.scss";

const paymentService = PaymentService.getInstance();

const paymentMethodLabel = (receipt: PaymentReceipt): string => {
  if (receipt.paymentMethodLabel) return receipt.paymentMethodLabel;
  if (receipt.paymentMethod === "CC") return "Credit Card";
  if (receipt.paymentMethod === "CK" || receipt.paymentMethod === "BANK") return "eCheck";
  return receipt.paymentMethod || "—";
};

const PaymentReceiptPage: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const history = useHistory();
  const [receipt, setReceipt] = useState<PaymentReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (!paymentId) {
      setError("Missing payment id.");
      setLoading(false);
      return;
    }
    setLoading(true);
    paymentService.getPaymentReceipt(paymentId).then((res) => {
      if (res.ok() && res.data) {
        setReceipt(res.data);
        setError(null);
      } else {
        setError(typeof res.getError?.() === "string" ? (res.getError() as string) : "Unable to load receipt");
      }
      setLoading(false);
    });
  }, [paymentId]);

  const onPdfAction = async (action: PdfDocumentAction) => {
    if (!paymentId) return;
    setPdfBusy(true);
    setError(null);
    try {
      const res = await paymentService.getPaymentReceiptPdf(paymentId);
      if (!res.ok() || !res.data) {
        throw new Error(
          typeof res.getError?.() === "string"
            ? (res.getError() as string)
            : "Failed to download receipt PDF"
        );
      }
      deliverPdf(res.data, `payment-receipt-${paymentId}.pdf`, action);
    } catch (e) {
      setError(getPdfDownloadErrorMessage(e, "Failed to download receipt PDF"));
    } finally {
      setPdfBusy(false);
    }
  };

  const dollars = receipt?.amountCents != null ? receipt.amountCents / 100 : undefined;

  return (
    <ContentContainer title="Payment receipt">
      <div className="payment-return">
        <Loading isLoading={loading} />
        {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
        {receipt && (
          <div className="payment-return__body">
            <div className="payment-return__receipt payment-receipt success">
              <XGSIcon className="payment-receipt__icon" icon={XGSIcons.faCheckCircle} />
              <div className="payment-receipt__message">Payment receipt</div>
              <div className="payment-receipt__transaction">Reference: {receipt.paymentId}</div>
              {dollars != null && (
                <PaymentSummary
                  amountCents={receipt.amountCents}
                  surchargeAmountCents={receipt.surchargeAmountCents}
                  invoices={receipt.invoices}
                />
              )}
              <div className="payment-receipt__details">
                {(receipt.dateUpdated || receipt.dateCreated) && (
                  <LabeledValue
                    label="DATE PAID"
                    value={
                      <Moment utc local format="MM/DD/YYYY h:mm A">
                        {receipt.dateUpdated || receipt.dateCreated}
                      </Moment>
                    }
                    direction={LabeledValueDirection.vertical}
                    valueSize={LabeledValueValueSize.medium}
                    extraClassName="payment-receipt__details__item"
                  />
                )}
                {receipt.paidBy && (
                  <LabeledValue
                    label="PAID BY"
                    value={receipt.paidBy}
                    direction={LabeledValueDirection.vertical}
                    valueSize={LabeledValueValueSize.medium}
                    extraClassName="payment-receipt__details__item"
                  />
                )}
                <LabeledValue
                  label="PAYMENT METHOD"
                  value={paymentMethodLabel(receipt)}
                  direction={LabeledValueDirection.vertical}
                  valueSize={LabeledValueValueSize.medium}
                  extraClassName="payment-receipt__details__item"
                />
                {receipt.customerLabel && (
                  <LabeledValue
                    label="CUSTOMER"
                    value={receipt.customerLabel}
                    direction={LabeledValueDirection.vertical}
                    valueSize={LabeledValueValueSize.medium}
                    extraClassName="payment-receipt__details__item"
                  />
                )}
                {receipt.merchantName && (
                  <LabeledValue
                    label="MERCHANT"
                    value={receipt.merchantName}
                    direction={LabeledValueDirection.vertical}
                    valueSize={LabeledValueValueSize.medium}
                    extraClassName="payment-receipt__details__item"
                  />
                )}
              </div>
              <InvoicesPaid invoices={receipt.invoices} />
              <div className="payment-receipt__bottom">
                If you have any questions, contact us at <b>info@xgsi.com</b> or call{" "}
                <b>(844) 947-7447</b>
              </div>
            </div>
            <div className="payment-return__actions no-print">
              <PdfActionSplitButton onAction={onPdfAction} spinner={pdfBusy}>
                Receipt
              </PdfActionSplitButton>
              <Button theme={ButtonThemes.gray} onClick={() => history.goBack()}>
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
    </ContentContainer>
  );
};

export default PaymentReceiptPage;
